import { useEffect, useState } from 'react';
import { adminApi } from '@/lib/api';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';
import clsx from 'clsx';
import { TrashIcon, BuildingOfficeIcon, UserIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface ConnectAccountInfo {
  id: string;
  email: string | null;
  businessProfile: {
    name: string | null;
    url: string | null;
  };
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  created: number;
  linkedTo: {
    type: 'organization' | 'affiliate' | null;
    id: string | null;
    name: string | null;
  };
  isOrphaned: boolean;
}

interface Stats {
  total: number;
  orphaned: number;
  linkedToOrganizations: number;
  linkedToAffiliates: number;
  active: number;
  pending: number;
}

export default function AdminStripeConnect() {
  const [accounts, setAccounts] = useState<ConnectAccountInfo[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'orphaned' | 'linked'>('all');
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  const fetchAccounts = async (cursor?: string) => {
    setIsLoading(true);
    try {
      const response = await adminApi.listConnectAccounts({
        limit: 50,
        startingAfter: cursor,
      });
      if (response.data.success) {
        const data = response.data.data;
        if (cursor) {
          setAccounts((prev) => [...prev, ...data.accounts]);
        } else {
          setAccounts(data.accounts);
        }
        setStats(data.stats);
        setHasMore(data.hasMore);
        setNextCursor(data.nextCursor);
      }
    } catch (error) {
      console.error('Failed to fetch Connect accounts:', error);
      toast.error('Failed to load Connect accounts');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  const handleDelete = async (account: ConnectAccountInfo) => {
    const linkedInfo = account.linkedTo.type
      ? `\n\nThis account is linked to ${account.linkedTo.type}: ${account.linkedTo.name || account.linkedTo.id}`
      : '\n\nThis is an orphaned account (not linked to any organization or affiliate).';

    const confirmed = window.confirm(
      `Are you sure you want to delete this Stripe Connect account?\n\n` +
      `Account: ${account.email || account.id}` +
      linkedInfo +
      '\n\nThis action cannot be undone.'
    );

    if (!confirmed) return;

    try {
      await adminApi.deleteConnectAccount(account.id, { forceDelete: false });
      toast.success('Connect account deleted successfully');
      // Remove from local state
      setAccounts((prev) => prev.filter((a) => a.id !== account.id));
      if (stats) {
        setStats({
          ...stats,
          total: stats.total - 1,
          orphaned: account.isOrphaned ? stats.orphaned - 1 : stats.orphaned,
          linkedToOrganizations: account.linkedTo.type === 'organization'
            ? stats.linkedToOrganizations - 1
            : stats.linkedToOrganizations,
          linkedToAffiliates: account.linkedTo.type === 'affiliate'
            ? stats.linkedToAffiliates - 1
            : stats.linkedToAffiliates,
        });
      }
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to delete Connect account';
      if (message.includes('pending balance')) {
        const forceConfirmed = window.confirm(
          'This account has a pending balance. Do you want to force delete it anyway?\n\n' +
          'Warning: Any pending payouts will be lost.'
        );
        if (forceConfirmed) {
          try {
            await adminApi.deleteConnectAccount(account.id, { forceDelete: true });
            toast.success('Connect account force deleted');
            setAccounts((prev) => prev.filter((a) => a.id !== account.id));
          } catch {
            toast.error('Failed to force delete account');
          }
        }
      } else {
        toast.error(message);
      }
    }
  };

  const filteredAccounts = accounts.filter((account) => {
    if (filter === 'orphaned') return account.isOrphaned;
    if (filter === 'linked') return !account.isOrphaned;
    return true;
  });

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-2xl font-bold text-charcoal">Stripe Connect Accounts</h2>
        <p className="text-charcoal/70 mt-1">
          Manage all Stripe Connect accounts, including orphaned accounts
        </p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-warm-white rounded-xl p-4 border border-cream">
            <p className="text-sm text-charcoal/60">Total Accounts</p>
            <p className="text-2xl font-bold text-charcoal">{stats.total}</p>
          </div>
          <div className="bg-warm-white rounded-xl p-4 border border-cream">
            <p className="text-sm text-charcoal/60">Orphaned</p>
            <p className="text-2xl font-bold text-rust">{stats.orphaned}</p>
          </div>
          <div className="bg-warm-white rounded-xl p-4 border border-cream">
            <p className="text-sm text-charcoal/60">Active</p>
            <p className="text-2xl font-bold text-sage">{stats.active}</p>
          </div>
          <div className="bg-warm-white rounded-xl p-4 border border-cream">
            <p className="text-sm text-charcoal/60">Pending Setup</p>
            <p className="text-2xl font-bold text-tan">{stats.pending}</p>
          </div>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="flex gap-2">
        {[
          { key: 'all', label: 'All Accounts' },
          { key: 'orphaned', label: `Orphaned (${stats?.orphaned || 0})` },
          { key: 'linked', label: 'Linked' },
        ].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setFilter(key as typeof filter)}
            className={clsx(
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              filter === key
                ? 'bg-sage text-white'
                : 'bg-cream text-charcoal hover:bg-tan/30'
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Orphaned Warning */}
      {filter === 'orphaned' && stats && stats.orphaned > 0 && (
        <div className="bg-rust/10 border border-rust/20 rounded-xl p-4 flex items-start gap-3">
          <ExclamationTriangleIcon className="w-5 h-5 text-rust flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-charcoal">Orphaned Accounts Found</p>
            <p className="text-sm text-charcoal/70 mt-1">
              These Stripe Connect accounts exist in Stripe but are not linked to any organization
              or affiliate in your database. They may be from deleted accounts or failed setups.
              You can safely delete orphaned accounts to clean up your Stripe dashboard.
            </p>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="rounded-xl bg-warm-white shadow-sm border border-cream overflow-hidden">
        {isLoading && accounts.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-sage"></div>
          </div>
        ) : filteredAccounts.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-charcoal/60">
              {filter === 'orphaned'
                ? 'No orphaned accounts found'
                : 'No Connect accounts found'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-cream/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-charcoal/70 uppercase tracking-wider">
                    Account
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-charcoal/70 uppercase tracking-wider">
                    Linked To
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-charcoal/70 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-charcoal/70 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-charcoal/70 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-cream">
                {filteredAccounts.map((account) => (
                  <tr
                    key={account.id}
                    className={clsx(
                      'hover:bg-cream/30 transition-colors',
                      account.isOrphaned && 'bg-rust/5'
                    )}
                  >
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-charcoal">
                          {account.businessProfile.name || account.email || 'Unnamed'}
                        </p>
                        <p className="text-xs text-charcoal/60">{account.id}</p>
                        {account.businessProfile.url && (
                          <p className="text-xs text-sage truncate max-w-[200px]">
                            {account.businessProfile.url}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {account.isOrphaned ? (
                        <span className="badge badge-danger flex items-center gap-1 w-fit">
                          <ExclamationTriangleIcon className="w-3 h-3" />
                          Orphaned
                        </span>
                      ) : account.linkedTo.type === 'organization' ? (
                        <div className="flex items-center gap-2">
                          <BuildingOfficeIcon className="w-4 h-4 text-charcoal/60" />
                          <div>
                            <p className="text-sm font-medium text-charcoal">
                              {account.linkedTo.name}
                            </p>
                            <p className="text-xs text-charcoal/60">Organization</p>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <UserIcon className="w-4 h-4 text-charcoal/60" />
                          <div>
                            <p className="text-sm font-medium text-charcoal">
                              {account.linkedTo.name || account.linkedTo.id}
                            </p>
                            <p className="text-xs text-charcoal/60">Affiliate</p>
                          </div>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1">
                        <span
                          className={clsx(
                            'badge w-fit',
                            account.chargesEnabled && account.payoutsEnabled
                              ? 'badge-success'
                              : 'badge-warning'
                          )}
                        >
                          {account.chargesEnabled && account.payoutsEnabled
                            ? 'Active'
                            : 'Pending Setup'}
                        </span>
                        {!account.detailsSubmitted && (
                          <span className="text-xs text-charcoal/60">
                            Details not submitted
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-charcoal/60 text-sm">
                      {account.created
                        ? format(new Date(account.created * 1000), 'MMM d, yyyy')
                        : '—'}
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleDelete(account)}
                        className="p-2 text-rust hover:bg-rust/20 rounded-lg transition-colors"
                        title="Delete Account"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Load More */}
        {hasMore && (
          <div className="p-4 border-t border-cream">
            <button
              onClick={() => nextCursor && fetchAccounts(nextCursor)}
              disabled={isLoading}
              className="btn btn-secondary w-full"
            >
              {isLoading ? 'Loading...' : 'Load More'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
